import { computed, ExtractPropTypes, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { logLlmEventToRum } from '@/libs/features/llm/logging'
import {
  CitedContextDatapoint,
  LlmDatadogLogEvent,
  LlmFeature,
  LlmModel,
  LlmSourceType,
} from '@/libs/features/llm/types'
import { useLlmSourcesStore } from '@/store/llm/askAProfile/store'
import { usePermissions } from '@/store/profile/permissions'
import { useWhoAmI } from '@/store/profile/whoAmI'
import { DevToolOptions } from '@/tokens/llm/LlmDevTools/types'
import { buildLlmDatadogLoggingDetails } from '@/utils/llm/loggingHelpers'
import {
  PREDEFINED_PROMPTS,
  PROMPT_START_TEXT,
  PredefinedPrompt,
  parseSectionTitle,
} from './prompts'
import { useAskAProfileApi } from './store'
import { ASK_A_PROFILE_PROPS, SectionResult } from './types'

type AskAProfileProps = ExtractPropTypes<typeof ASK_A_PROFILE_PROPS>

/**
 *
 * @param props
 * @param props.patientPerson
 * @param props.showDevTools
 */
export default function (props: AskAProfileProps) {
  const llmApi = useAskAProfileApi()
  const llmResponses = ref<SectionResult[] | undefined>(undefined)
  const selectedPrompt = ref<PredefinedPrompt | null>(null)
  const inputText = ref('')
  const loading = ref(false)
  const hasError = ref(false)
  const devToolOptions = ref<DevToolOptions>({})
  const { selfEntity, roles } = useWhoAmI()
  const { canUseLlmDevTools } = usePermissions()
  const route = useRoute()
  const llmSourcesStore = useLlmSourcesStore()

  const LLM_QUOTABLE_SOURCE_TYPES = new Set([
    LlmSourceType.COMMUNICATION,
    LlmSourceType.SUBTASK,
    LlmSourceType.GOAL,
  ])

  /**
   *
   * @param citations - list of citations to populate the store with.
   */
  const populateQuotesStore = (citations: CitedContextDatapoint[]) => {
    for (const source of citations) {
      if (
        source.sourceId &&
        source.validatedQuotes?.length &&
        LLM_QUOTABLE_SOURCE_TYPES.has(source.sourceType)
      ) {
        llmSourcesStore.setQuotesForSource(
          source.sourceId,
          source.validatedQuotes.map((vq) => vq.quote)
        )
      }
    }
  }

  /**
   *
   * @param query
   * @param systemPrompt - overrides the backend's default system prompt.
   */
  function buildRequestBody(query: string, systemPrompt?: string) {
    return {
      query,
      patientId: props.patientPerson?.entityId,
      ...devToolOptions.value,
      // Use dev tools prompts to override
      system: devToolOptions.value.system || systemPrompt,
      debug: props.showDevTools ?? canUseLlmDevTools.value,
    }
  }

  /**
   *
   */
  async function submit() {
    const subprompts = selectedPrompt.value?.subprompts
    // Multi-prompts fire one call per subprompt, in parallel
    const entries: { title: string; query: string }[] = subprompts
      ? subprompts.map((sub) => ({
          title: parseSectionTitle(sub),
          query: `${PROMPT_START_TEXT} ${sub}`,
        }))
      : [{ title: '', query: inputText.value }]

    const systemPrompt = selectedPrompt.value?.systemPrompt

    llmSourcesStore.clearAll()
    llmResponses.value = undefined
    hasError.value = false
    loading.value = true

    // await all responses in parallel
    const results = await Promise.allSettled(
      entries.map(({ query }) =>
        llmApi.create({ body: buildRequestBody(query, systemPrompt) })
      )
    )

    llmResponses.value = results.map((result, index) => {
      if (result.status === 'rejected') {
        return { title: entries[index].title, error: true }
      }
      const response = result.value
      populateQuotesStore(response.citations || [])
      const loggingDetails = buildLlmDatadogLoggingDetails({
        response,
        route,
        feature: LlmFeature.ASK_A_PROFILE,
        query: entries[index].query,
        devToolOptions: devToolOptions.value,
        person: selfEntity.value?.person,
        roles: roles.value,
      })
      if (loggingDetails) {
        logLlmEventToRum(LlmDatadogLogEvent.QUERY, loggingDetails)
      }
      return {
        title: entries[index].title,
        response,
        loggingDetails,
        error: false,
      }
    })

    loading.value = false
  }

  /**
   *
   */
  async function reset() {
    llmResponses.value = undefined
    llmSourcesStore.clearAll()
  }

  /**
   *
   */
  function updateInputText() {
    const prompt = selectedPrompt.value
    // Multi-prompts build their queries from the subprompts at submit time
    if (!prompt || prompt.subprompts) {
      inputText.value = ''
      return
    }
    const helpText = prompt.llmHelpText ? ` (${prompt.llmHelpText})` : ''
    inputText.value = `${PROMPT_START_TEXT} ${prompt.text}.${helpText}`
  }

  /**
   *
   */
  async function submitDeepDive() {
    inputText.value =
      inputText.value +
      ' Make sure to access data from ALL AVAILABLE SOURCES AND TOOLS.'
    devToolOptions.value.model = LlmModel.CLAUDE_SONNET_4_5
    await submit()
  }

  const memberName = computed(() => {
    if (props.patientPerson) {
      return props.patientPerson.preferredName ?? props.patientPerson.firstName
    }
    return 'this member'
  })

  const patientId = computed(() => {
    return props.patientPerson?.entityId
  })

  // Multi-prompt queries come from the prompt itself, not the input text
  const isMultiPrompt = computed(() => !!selectedPrompt.value?.subprompts)

  const canSubmit = computed(
    () => !loading.value && (isMultiPrompt.value || !!inputText.value)
  )

  const promptsToOptions = PREDEFINED_PROMPTS.map((prompt) => ({
    label: prompt.text,
    value: prompt,
  }))

  const loadingMessages = [
    { text: 'Analyzing applicable tools...', ms: 1200 },
    { text: 'Reading member data...', ms: 2500 },
    { text: 'Generating results...', ms: 3000 },
  ]

  watch(() => props.patientPerson?.entityId, reset, { immediate: true })

  return {
    inputText,
    llmResponses,
    loading,
    hasError,
    memberName,
    patientId,
    isMultiPrompt,
    canSubmit,
    submit,
    reset,
    devToolOptions,
    promptsToOptions,
    selectedPrompt,
    updateInputText,
    submitDeepDive,
    loadingMessages,
  }
}
